import { data } from '@amazon/vinyl-observable'
import { type BridgeTransport, ConnectionStatus, type ProtocolVersion } from '../bridge/protocol'

/** What a playback host sets on the web view's window before the page's scripts run. */
export interface PlaybackHostDescriptor {
    readonly protocols: readonly ProtocolVersion[]
}

/** The parts of a React Native web view's window the client transport uses. */
export interface WebViewWindow extends Pick<
    EventTarget,
    'addEventListener' | 'removeEventListener'
> {
    ReactNativeWebView?: { postMessage(message: string): void }
    __ampPlaybackHost?: PlaybackHostDescriptor
    /** The host delivers each message by calling `receive` through `injectJavaScript`. */
    __ampPlaybackBridge?: { receive(message: string): void }
}

/**
 * The web view's end of a React Native WebView link to a playback host: messages go out through
 * `ReactNativeWebView.postMessage` and come in through `__ampPlaybackBridge.receive`. Null outside
 * a web view, and unless a host announced itself with `__ampPlaybackHost`; a caller that knows a
 * host may be there but can't rely on the announcement arriving before boot passes
 * `requireAnnouncement: false`, and the handshake's hello is the probe. The link is down while the
 * page is hidden for good (`pagehide`), and back on `pageshow`.
 */
export function createWebViewClientTransport(
    win: WebViewWindow = window,
    { requireAnnouncement = true }: { readonly requireAnnouncement?: boolean } = {}
): BridgeTransport | null {
    const native = win.ReactNativeWebView
    if (!native || (requireAnnouncement && !win.__ampPlaybackHost)) return null
    const status$ = data(ConnectionStatus.CONNECTED)
    const listeners = new Set<(message: string) => void>()
    const bridge = {
        receive(message: string) {
            for (const listener of [...listeners]) listener(message)
        },
    }
    win.__ampPlaybackBridge = bridge
    const onPageHide = () => (status$.value = ConnectionStatus.DISCONNECTED)
    const onPageShow = () => (status$.value = ConnectionStatus.CONNECTED)
    win.addEventListener('pagehide', onPageHide)
    win.addEventListener('pageshow', onPageShow)
    return {
        status$,
        send: (message) => native.postMessage(message),
        onMessage(listener) {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
        dispose() {
            win.removeEventListener('pagehide', onPageHide)
            win.removeEventListener('pageshow', onPageShow)
            if (win.__ampPlaybackBridge === bridge) delete win.__ampPlaybackBridge
            listeners.clear()
            status$.value = ConnectionStatus.DISCONNECTED
        },
    }
}
